import React, { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { patch } from "@rails/request.js";
import { motion } from "framer-motion";

import TipTapArea from "./TipTapArea";
import SliderToggle from "./../shared/SliderToggle";
import RadioButtons from "./../shared/RadioButtons";
import { Toasts, useToasts } from "./../shared/Toast";
import EditableName from "./../shared/EditableName";
import ViewControls from "./../shared/ViewControls";
import Preview from "./Preview";
import { ExplainerScreen } from "./../shared/ExplainerScreen";

export const CapformEdit = ({ initCapform, backUrl, projectLabels = [], labelsUrl }) => {
  const [capform, setCapform] = useState(initCapform.capform);
  // Labels live outside react-hook-form because the user can also type
  // brand-new ones — they don't exist in projectLabels yet, so we keep
  // the array as React state and merge with the canonical pool at render.
  const initialLabelNames = (Array.isArray(initCapform.capform.default_labels)
    ? initCapform.capform.default_labels.map((l) => l.name)
    : []);
  const [defaultLabelNames, setDefaultLabelNames] = useState(initialLabelNames);
  const [labelDraft, setLabelDraft] = useState("");
  const { toasts, setToasts, addToast } = useToasts();
  const [page, setPage] = useState("welcome-page");
  const [previewMode, setPreviewMode] = useState("desktop");
  const [breakoutOpen, setBreakoutOpen] = useState(false);
  const {
    formState,
    register,
    watch,
    reset,
    handleSubmit,
    setValue,
    getValues,
  } = useForm({
    defaultValues: {
      welcome_title: capform.welcome_title,
      welcome_text: capform.welcome_text,
      prompt_text: capform.prompt_text,
      allow_ratings: capform.allow_ratings,
      allow_images: capform.allow_images,
      auto_approve: capform.auto_approve,
      auto_transcribe: capform.auto_transcribe,
      collect_email: capform.collect_email,
      collect_avatar: capform.collect_avatar,
      collect_job_title: capform.collect_job_title,
      collect_company_name: capform.collect_company_name,
      collect_company_logo: capform.collect_company_logo,
      collect_company_team: capform.collect_company_team,
      custom_thanks_page: capform.custom_thanks_page,
      thanks_title: capform.thanks_title,
      thanks_text: capform.thanks_text,
      thanks_url: capform.thanks_url,
      background_style: capform.background_style || "wavey",
      background_color: capform.background_color || "#3E8DFF",
    },
  });

  const { isDirty, isLoading } = formState;

  const handleUpdate = (key, val) => {
    setValue(key, val, { shouldDirty: true, shouldValidate: true });
  };

  // ── Autosave ─────────────────────────────────────────────────────────
  // The survey editor saves on its own; this one used to need the Save
  // button pressed. Every change now schedules a save, debounced so a
  // burst of keystrokes lands as one request rather than one per letter.
  const saveTimer  = useRef(null);
  const savedTimer = useRef(null);
  const submitRef  = useRef(null);
  // onSubmit calls reset() and setDefaultLabelNames() with the server's
  // response. Both of those fire the very watchers that schedule a save,
  // so without this the editor saves itself in a loop forever.
  const applyingResponse = useRef(false);

  // One toast per burst rather than one per field.
  const notifySaved = () => {
    if (savedTimer.current) clearTimeout(savedTimer.current);
    savedTimer.current = setTimeout(() => {
      savedTimer.current = null;
      addToast("success", "Saved");
    }, 600);
  };

  const scheduleSave = () => {
    if (applyingResponse.current) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      saveTimer.current = null;
      submitRef.current?.();
    }, 800);
  };

  // Run any pending save now — used before navigating away, so a change
  // made a moment ago isn't lost to the debounce window.
  const flushSave = async () => {
    if (!saveTimer.current) return;
    clearTimeout(saveTimer.current);
    saveTimer.current = null;
    await submitRef.current?.();
  };

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (savedTimer.current) clearTimeout(savedTimer.current);
  }, []);

  useEffect(() => {
    // Merge into prev — replacing with `value` (which only contains
    // react-hook-form registered fields) drops capform.name (owned by
    // the separate <EditableName>) and the header falls back to
    // "untitled form" on every keystroke.
    const subscription = watch((value) => {
      setCapform((prev) => ({ ...prev, ...value }));
      scheduleSave();
    });
    return () => subscription.unsubscribe();
  }, [watch]);

  const inFlight = useRef(false);

  const onSubmit = async (data) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const response = await patch(`/api/v1/capforms/${initCapform.capform.id}`, {
      body: {
        capform: {
          welcome_title: data.welcome_title,
          welcome_text: data.welcome_text,
          prompt_text: data.prompt_text,
          thanks_title: data.thanks_title,
          auto_approve: data.auto_approve,
          auto_transcribe: data.auto_transcribe,
          thanks_text: data.thanks_text,
          thanks_url: data.thanks_url,
          // Server find-or-creates by name scoped to the project. The
          // chip selections + any free-typed names live in the same
          // array — the user doesn't have to know the difference.
          default_label_names: defaultLabelNames,
          data: data,
        },
      },
      contentType: "application/json",
      responseKind: "json",
    });
    if (response.ok) {
      const json = await response.json;
      applyingResponse.current = true;
      reset(json.capform);
      setCapform(json.capform);
      if (Array.isArray(json.capform.default_labels)) {
        const names = json.capform.default_labels.map((l) => l.name);
        // Only replace when the contents actually differ — a fresh array
        // with identical names still re-runs the effect that watches it.
        setDefaultLabelNames((prev) =>
          prev.length === names.length && prev.every((n, i) => n === names[i]) ? prev : names
        );
      }
      // Release after the resulting renders have flushed, so none of the
      // watchers fired by reset() see the guard already lifted.
      setTimeout(() => { applyingResponse.current = false; }, 0);
      notifySaved();
    } else {
      addToast("error", "Error saving form");
    }
    inFlight.current = false;
  };

  submitRef.current = handleSubmit(onSubmit);

  // Default labels live outside react-hook-form, so `watch` never sees
  // them. Schedule a save when they change, skipping the first render.
  const labelsReady = useRef(false);
  useEffect(() => {
    if (!labelsReady.current) { labelsReady.current = true; return; }
    scheduleSave();
  }, [ defaultLabelNames ]);

  const toggleLabel = (name) => {
    setDefaultLabelNames((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const commitLabelDraft = () => {
    const name = labelDraft.trim();
    if (!name) return;
    setDefaultLabelNames((prev) => (prev.includes(name) ? prev : [...prev, name]));
    setLabelDraft("");
  };

  const welcome_text = getValues().welcome_text;
  const prompt_text = getValues().prompt_text;
  const auto_approve = getValues().auto_approve;
  const auto_transcribe = getValues().auto_transcribe;
  const collect_email = getValues().collect_email;
  const collect_avatar = getValues().collect_avatar;
  const collect_job_title = getValues().collect_job_title;
  const collect_company_team = getValues().collect_company_team;
  const collect_company_logo = getValues().collect_company_logo;
  const collect_company_name = getValues().collect_company_name;
  const custom_thanks_page = getValues().custom_thanks_page;
  const thanks_text = getValues().thanks_text;
  const background_style = getValues().background_style;
  const background_color = getValues().background_color;

  const publicUrl = `/cf/${initCapform.capform.uuid}`;
  // backUrl looks like "/projects/:project_id/capforms" — append the
  // capform id + share to land on the share page.
  const shareUrl = `${backUrl || ""}/${initCapform.capform.id}/share`;

  // Nothing to prompt about now — flush whatever the debounce is still
  // holding, then go.
  const openBreakout = async (e) => {
    e?.preventDefault();
    await flushSave();
    setBreakoutOpen(true);
  };

  const goToShare = async (e) => {
    e?.preventDefault();
    await flushSave();
    window.location.href = shareUrl;
  };

  const closeBreakout = () => setBreakoutOpen(false);

  useEffect(() => {
    if (!breakoutOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") closeBreakout(); };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [breakoutOpen]);

  const sections = [
    { id: "welcome-page",  label: "welcome",  color: "text-ssblue",   accent: "#3E8DFF" },
    { id: "prompt-page",   label: "prompt",   color: "text-ssgreen",  accent: "#3EB567" },
    { id: "contact-page",  label: "contact",  color: "text-ssorange", accent: "#FF8F47" },
    { id: "thanks-page",   label: "thanks",   color: "text-sspink",   accent: "#FF7AAC" },
    { id: "design-page",   label: "design",   color: "text-sspurple", accent: "#8a74b9" },
    { id: "settings-page", label: "settings", color: "text-ssred",    accent: "#EF4444" },
  ];

  const contactFields = [
    { key: "collect_email",        label: "Email",              hint: "Say thanks to those who send in a submission.",      value: collect_email },
    { key: "collect_avatar",       label: "Avatar",             hint: "Pictures are the best. Collect them.",               value: collect_avatar },
    { key: "collect_job_title",    label: "Job Title",          hint: "You could make a widget grouped by job title!",      value: collect_job_title },
    { key: "collect_company_name", label: "Company Name",       hint: "Highlight submissions by particular companies.",     value: collect_company_name },
    { key: "collect_company_logo", label: "Company Logo",       hint: "Great for logo marquees.",                           value: collect_company_logo },
    { key: "collect_company_team", label: "Company Department", hint: "Display collected kinds by teams.",                  value: collect_company_team },
  ];

  return (
    <div className="h-full flex flex-col">
      <Toasts toasts={toasts} setToasts={setToasts} />

      <header className="flex-none border-b-[3px] border-black bg-white">
        <div className="flex items-center gap-2 sm:gap-3 px-3 sm:px-5 py-2.5">
          <a
            href={backUrl || "/projects"}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white border-[2px] border-black text-slate-700 hover:bg-ssyellow hover:text-slate-900 shadow-[2px_2px_0_rgba(0,0,0,0.25)] hover:shadow-[3px_3px_0_rgba(0,0,0,0.35)] hover:-translate-y-0.5 transition-all"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-3.5 w-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5 8.25 12l7.5-7.5" />
            </svg>
            <span className="hidden sm:inline">back to capture forms</span>
            <span className="sm:hidden">back</span>
          </a>

          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="hidden md:inline zz-hand text-lg text-ssgreen leading-none flex-none">editing —</span>
            <EditableName
              endpoint={`/api/v1/capforms/${initCapform.capform.id}`}
              resourceKey="capform"
              name={capform.name}
              fallback="untitled form"
              onChange={(name) => setCapform((prev) => ({ ...prev, name }))}
              onError={(msg) => addToast("error", msg)}
              onSuccess={() => addToast("success", "Name updated")}
            />
          </div>

          <div className="flex-none flex items-center gap-1.5 sm:gap-2">
            <ViewControls
              mode={previewMode}
              setMode={setPreviewMode}
              onBreakout={openBreakout}
              breakoutActive={breakoutOpen}
            />
            {/* Saving happens on its own now; this reports state and lets
                you force the pending save through rather than waiting. */}
            <button
              type="button"
              onClick={() => flushSave()}
              className="zz-btn sm blue inline-flex items-center gap-1.5"
              title="Changes save automatically"
            >
              {isLoading && (
                <svg className="spinner animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
              )}
              {isDirty ? "saving…" : "saved ✓"}
            </button>
            <button
              type="button"
              onClick={goToShare}
              className="zz-btn sm green"
            >
              share
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="flex flex-col md:flex-row gap-4 sm:gap-5 p-3 sm:p-5">
          <aside className="w-full md:w-1/2 md:flex-none">
            <div className="bg-white border-[3px] border-black md:h-[calc(100vh-160px)] md:overflow-y-auto">
              <div className="divide-y-[3px] divide-black">
                {sections.map((s) => {
                  const isOpen = page === s.id;
                  return (
                    <div key={s.id}>
                      <button
                        type="button"
                        onClick={() => setPage(isOpen ? null : s.id)}
                        aria-expanded={isOpen}
                        className={`w-full px-5 py-4 flex justify-between items-center text-left transition-colors ${isOpen ? "bg-slate-50" : "hover:bg-slate-50"}`}
                      >
                      <span className={`zz-hand text-2xl ${s.color}`}>{s.label}</span>
                      <span
                        className={`text-xl font-black transition-transform ${isOpen ? "rotate-90" : ""}`}
                        style={{ color: s.accent }}
                        aria-hidden="true"
                      >
                        ›
                      </span>
                    </button>
                    <motion.div
                      initial={false}
                      animate={{
                        height: isOpen ? "auto" : 0,
                        display: isOpen ? "block" : "none",
                        opacity: isOpen ? 1 : 0,
                      }}
                      className="relative z-20 overflow-hidden"
                    >
                      {s.id === "welcome-page" && (
                        <div className="flex flex-col gap-y-4 p-4">
                          <div>
                            <label className="zz-label">Welcome title</label>
                            <input
                              type="text"
                              {...register("welcome_title", { required: false })}
                              className="zz-input"
                            />
                          </div>
                          <div>
                            <label className="zz-label">Welcome message</label>
                            <TipTapArea
                              html={welcome_text}
                              updateHtml={(val) => handleUpdate("welcome_text", val)}
                            />
                          </div>
                        </div>
                      )}

                      {s.id === "prompt-page" && (
                        <div className="flex flex-col gap-y-3 p-4">
                          <label className="zz-label">Prompt</label>
                          <TipTapArea
                            html={prompt_text}
                            updateHtml={(val) => handleUpdate("prompt_text", val)}
                          />
                        </div>
                      )}

                      {s.id === "contact-page" && (
                        <div className="flex flex-col gap-y-3 p-4">
                          <p className="text-sm text-slate-600">
                            Gather info about who is filling out the form.
                          </p>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-5">
                            {contactFields.map((f) => (
                              <div key={f.key} className="flex flex-col gap-y-1">
                                <div className="font-black text-slate-900">{f.label}</div>
                                <div className="text-xs text-slate-500">{f.hint}</div>
                                <RadioButtons
                                  kind={f.key}
                                  buttons={["Required", "Optional", "Do not collect"]}
                                  selected={f.value}
                                  setSelected={(val) => handleUpdate(f.key, val)}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {s.id === "thanks-page" && (
                        <div className="flex flex-col gap-y-4 p-4">
                          <div>
                            <p className="zz-label mb-2">After submit, send them to</p>
                            <div className="w-72 max-w-full">
                              <SliderToggle
                                choices={["Standard Page", "Custom Page"]}
                                selected={custom_thanks_page}
                                setSelected={(val) => handleUpdate("custom_thanks_page", val)}
                              />
                            </div>
                          </div>

                          {custom_thanks_page === "Standard Page" && (
                            <>
                              <div>
                                <label className="zz-label">Thanks title</label>
                                <input
                                  type="text"
                                  {...register("thanks_title", { required: false })}
                                  className="zz-input"
                                />
                              </div>
                              <div>
                                <label className="zz-label">Thanks message</label>
                                <TipTapArea
                                  html={thanks_text}
                                  updateHtml={(val) => handleUpdate("thanks_text", val)}
                                />
                              </div>
                            </>
                          )}

                          {custom_thanks_page === "Custom Page" && (
                            <div>
                              <label className="zz-label">Destination URL</label>
                              <div className="relative">
                                <input
                                  type="text"
                                  {...register("thanks_url", { required: true })}
                                  className="zz-input pl-16"
                                  placeholder="www.shosay.com/thanks"
                                />
                                <span className="pointer-events-none absolute inset-y-0 left-0 flex items-center justify-center pl-3 text-sm font-bold text-slate-400">
                                  https://
                                </span>
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {s.id === "design-page" && (
                        <div className="flex flex-col gap-y-4 p-4">
                          <div>
                            <p className="zz-label mb-2">Background pattern</p>
                            <RadioButtons
                              kind="background_style"
                              buttons={["wavey", "bubbles", "speech", "stars", "retro", "confetti", "gradient", "dots", "doodles", "solid"]}
                              selected={background_style}
                              setSelected={(val) => handleUpdate("background_style", val)}
                            />
                          </div>
                          <div>
                            <p className="zz-label mb-2">Background color</p>
                            <div className="flex items-center gap-3 flex-wrap">
                              <input
                                type="color"
                                value={background_color || "#3E8DFF"}
                                onChange={(e) => handleUpdate("background_color", e.target.value)}
                                className="h-12 w-16 border-[3px] border-black cursor-pointer bg-white"
                              />
                              <span className="text-xs font-mono font-bold text-slate-600">{(background_color || "#3E8DFF").toUpperCase()}</span>
                              {background_color && background_color.toLowerCase() !== "#3e8dff" && (
                                <button
                                  type="button"
                                  onClick={() => handleUpdate("background_color", "#3E8DFF")}
                                  className="zz-link text-xs"
                                >
                                  reset
                                </button>
                              )}
                            </div>
                            <p className="mt-2 text-xs text-slate-500">
                              Applies to most styles. Retro and gradient keep their curated palettes.
                            </p>
                          </div>
                        </div>
                      )}

                      {s.id === "settings-page" && (
                        <div className="flex flex-col gap-y-5 p-4">
                          <div className="flex flex-col gap-y-2">
                            <div className="font-black text-slate-900">Approve submissions</div>
                            <div className="text-xs text-slate-500">Pick between reviewing submissions yourself or auto-publishing them.</div>
                            <SliderToggle
                              choices={["Manual Approval", "Auto Approval"]}
                              selected={auto_approve}
                              setSelected={(val) => handleUpdate("auto_approve", val)}
                            />
                          </div>
                          <div className="flex flex-col gap-y-2">
                            <div className="font-black text-slate-900">Auto-transcribe video</div>
                            <div className="text-xs text-slate-500">Generate captions automatically for video submissions.</div>
                            <SliderToggle
                              choices={["Transcribe", "Do not transcribe"]}
                              selected={auto_transcribe}
                              setSelected={(val) => handleUpdate("auto_transcribe", val)}
                            />
                          </div>
                          <div className="flex flex-col gap-y-2">
                            <div className="font-black text-slate-900">Auto-apply labels</div>
                            <div className="text-xs text-slate-500">
                              Every proof submitted via this form gets these labels. Use them later to filter widgets and walls.
                              {labelsUrl && (<> Manage your labels <a href={labelsUrl} className="zz-link">here</a>.</>)}
                            </div>

                            {(() => {
                              // Pool = every project label + any free-typed names
                              // not yet saved as a Label (the latter only matter
                              // when the user types something brand-new).
                              const pool = Array.from(new Set([
                                ...projectLabels.map((l) => l.name),
                                ...defaultLabelNames,
                              ])).sort();
                              return pool.length === 0 ? (
                                <p className="text-xs text-slate-500 italic">No labels in this project yet — type one below.</p>
                              ) : (
                                <div className="flex flex-wrap gap-1.5">
                                  {pool.map((name) => {
                                    const on = defaultLabelNames.includes(name);
                                    return (
                                      <button
                                        key={name}
                                        type="button"
                                        onClick={() => toggleLabel(name)}
                                        title={on ? "Auto-applied — click to remove" : "Click to auto-apply"}
                                        className={
                                          "px-2.5 py-1 text-xs font-bold rounded-full border-[2px] transition-colors inline-flex items-center gap-1 " +
                                          (on
                                            ? "border-black bg-sspink text-white"
                                            : "border-slate-300 bg-white text-slate-400 hover:text-slate-700 hover:border-slate-500")
                                        }
                                      >
                                        {on && <span aria-hidden="true" className="leading-none">✓</span>}
                                        <span>{name}</span>
                                      </button>
                                    );
                                  })}
                                </div>
                              );
                            })()}

                            <div className="flex gap-2 mt-1">
                              <input
                                type="text"
                                value={labelDraft}
                                onChange={(e) => setLabelDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commitLabelDraft(); } }}
                                placeholder="add new label —"
                                className="zz-input flex-1"
                              />
                              <button type="button" onClick={commitLabelDraft} className="zz-btn sm pink">add</button>
                            </div>
                          </div>
                        </div>
                      )}
                    </motion.div>
                  </div>
                );
              })}
              </div>
            </div>

            <div className="mt-3 px-1 text-xs">
              <ExplainerScreen
                title="What are Capture Forms?"
                open={typeof window !== "undefined" && new URLSearchParams(window.location.search).get("created") === "true"}
              />
            </div>
          </aside>

          <section className="w-full md:flex-1 min-w-0">
            <Preview capform={capform} page={page} previewMode={previewMode} />
          </section>
        </div>
      </div>

      {breakoutOpen && (
        <div className="fixed inset-0 z-[100] bg-black">
          <iframe
            src={publicUrl}
            className="w-full h-full block bg-white border-0"
            title="Capture form fullscreen preview"
          />
          <button
            type="button"
            onClick={closeBreakout}
            className="absolute top-4 right-4 z-10 inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold uppercase tracking-wider bg-white border-[2px] border-black text-slate-900 hover:bg-ssyellow shadow-[3px_3px_0_rgba(0,0,0,0.4)] transition-colors"
          >
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth="2.5" stroke="currentColor" className="h-3.5 w-3.5">
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
            </svg>
            exit fullscreen
            <span className="hidden sm:inline opacity-50 ml-1">esc</span>
          </button>
        </div>
      )}
    </div>
  );
};
